// =============================================================================
// Panel de cobro: métodos de pago (efectivo/tarjeta/yappy/fiado/mixto), vuelto,
// cliente (obligatorio si hay fiado), y emisión de factura electrónica. Arma
// el array de payments que consume la cola offline (pos_finalize_sale valida
// Σpagos − vuelto = total en el servidor).
// =============================================================================
import { useMemo, useState } from "react";
import { money } from "../lib/utils";
import { computePayment } from "../lib/payment";
import type { PaymentInput, Customer } from "../lib/api";

type Method = "cash" | "card" | "yappi" | "credit";
const METHODS: { id: Method; label: string; icon: string }[] = [
  { id: "cash", label: "Efectivo", icon: "💵" },
  { id: "card", label: "Tarjeta", icon: "💳" },
  { id: "yappi", label: "Yappy", icon: "📱" },
  { id: "credit", label: "Fiado", icon: "📒" },
];

export interface CheckoutResult {
  payments: PaymentInput[];
  changeGiven: number;
  emitInvoice: boolean;
  customerId: string | null;
}

export function CheckoutPanel({
  total, online, hasCashSession = true, customer,
  onPickCustomer, onConfirm, onCancel,
}: {
  total: number;
  online: boolean;
  /** ¿Hay una sesión de caja abierta? El efectivo la exige (arqueo). */
  hasCashSession?: boolean;
  /** Cliente seleccionado en el POS (opcional; obligatorio con fiado). */
  customer?: Customer | null;
  /** Callback para abrir el selector de cliente (lo dueño es el POS). */
  onPickCustomer?: () => void;
  onConfirm: (r: CheckoutResult) => void | Promise<void>;
  onCancel: () => void;
}) {
  const [amounts, setAmounts] = useState<Record<Method, string>>({ cash: total.toFixed(2), card: "", yappi: "", credit: "" });
  const [emitInvoice, setEmitInvoice] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const paid = useMemo(
    () => (Object.keys(amounts) as Method[]).reduce((s, m) => s + (parseFloat(amounts[m]) || 0), 0),
    [amounts],
  );
  const cashAmt = parseFloat(amounts.cash) || 0;
  const creditAmt = parseFloat(amounts.credit) || 0;
  const { change, enough, nonCashOverpay } = computePayment({ cash: cashAmt, nonCash: paid - cashAmt, total });
  const creditOffline = creditAmt > 0 && !online;
  const cashWithoutSession = cashAmt > 0 && !hasCashSession;
  // Fiado exige un cliente (server-side también, pero validamos UX antes).
  const creditNeedsCustomer = creditAmt > 0 && !customer;

  function set(m: Method, v: string) { setAmounts((a) => ({ ...a, [m]: v })); }

  async function confirm() {
    if (submitting) return;
    setSubmitting(true);
    const payments: PaymentInput[] = (Object.keys(amounts) as Method[])
      .map((m) => ({ method: m, amount: parseFloat(amounts[m]) || 0 }))
      .filter((p) => p.amount > 0);
    try {
      await onConfirm({ payments, changeGiven: change, emitInvoice, customerId: customer?.id ?? null });
    } finally { setSubmitting(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-baseline justify-between">
          <h2 className="text-lg font-bold">Cobrar</h2>
          <span className="text-2xl font-bold">{money(total)}</span>
        </div>

        {/* cliente: visible si está la prop de picker (POS lo provee) */}
        {onPickCustomer && (
          <button onClick={onPickCustomer}
            className="mb-3 flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm hover:bg-grafito-50">
            <div>
              <span className="text-grafito-400">Cliente: </span>
              <span className="font-semibold">{customer?.name ?? "Consumidor final"}</span>
            </div>
            <span className="text-grafito-400">›</span>
          </button>
        )}

        <div className="space-y-2">
          {METHODS.map((m) => (
            <div key={m.id} className="flex items-center gap-3">
              <span className="w-24 text-sm">{m.icon} {m.label}</span>
              <input
                type="number" inputMode="decimal" step="0.01" min="0"
                value={amounts[m.id]} onChange={(e) => set(m.id, e.target.value)}
                placeholder="0.00"
                className="flex-1 rounded-lg border px-3 py-2 text-right outline-none focus:border-brand"
              />
            </div>
          ))}
        </div>

        <div className="mt-4 space-y-1 text-sm">
          <Row label="Pagado" value={money(paid)} />
          <Row label="Vuelto (de efectivo)" value={money(change)} accent={change > 0} />
        </div>

        {nonCashOverpay && (
          <p className="mt-2 text-sm text-red-600">Tarjeta/Yappy/fiado no pueden exceder el total (no hay vuelto de esos métodos).</p>
        )}
        {creditOffline && (
          <p className="mt-2 text-sm text-offline">Sin conexión no se puede fiar (no se verifica el cupo).</p>
        )}
        {cashWithoutSession && (
          <p className="mt-2 text-sm text-amber-700">Abre la caja antes de cobrar en efectivo (requerido para el arqueo).</p>
        )}
        {creditNeedsCustomer && (
          <p className="mt-2 text-sm text-amber-700">El fiado requiere un cliente con cuenta de crédito.</p>
        )}

        <label className="mt-4 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={emitInvoice} onChange={(e) => setEmitInvoice(e.target.checked)} disabled={!online} />
          Emitir factura electrónica {!online && <span className="text-grafito-400">(requiere conexión)</span>}
        </label>

        <div className="mt-6 flex gap-2">
          <button onClick={onCancel} className="btn btn-ghost flex-1">Cancelar</button>
          <button
            onClick={confirm}
            disabled={!enough || nonCashOverpay || creditOffline || cashWithoutSession || creditNeedsCustomer || submitting}
            className="btn btn-primary flex-1 disabled:opacity-40"
          >
            {submitting ? "Procesando…" : "Confirmar cobro"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-grafito-400">{label}</span>
      <span className={accent ? "font-semibold text-brand" : ""}>{value}</span>
    </div>
  );
}
